import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../shared/Icon';
import SectionHead from '../shared/SectionHead';
import { useReveal } from '../../hooks/useReveal';

const STEP_IDS = ['imagem', 'linhas', 'falhas', 'relatorio'];
const STEP_IMG = {
  imagem: '/assets/pixviewer_gaps_overview.webp',
  linhas: '/assets/linhas.webp',
  falhas: '/assets/falhas.webp',
  relatorio: '/assets/relatorio.webp',
};

export default function ProductTour() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const ref = useReveal({ stagger: 0.08 });

  const currentId = STEP_IDS[step];
  const currentTitle = t(`home.product_tour.steps.${currentId}.title`);
  const currentDesc = t(`home.product_tour.steps.${currentId}.desc`);
  const currentTags = t(`home.product_tour.steps.${currentId}.tags`, { returnObjects: true });
  const stepLabel = (i) => `${String(i + 1).padStart(2, '0')} · ${t(`home.hero.layers.${STEP_IDS[i]}.label`)}`;

  return (
    <section
      ref={ref}
      id="tour"
      className="section"
      style={{ background: 'var(--p2a-dark-bg)', borderBottom: '1px solid var(--p2a-dark-border)' }}
    >
      <div className="p2-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 32, flexWrap: 'wrap', marginBottom: 48 }}>
          <SectionHead
            eyebrow={t('home.product_tour.eyebrow')}
            title={t('home.product_tour.title')}
            lede={t('home.product_tour.lede')}
            max={560}
          />
          <div className="mono reveal" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--p2a-dark-fg-4)' }}>
            {t('home.product_tour.stage_prefix')} {String(step + 1).padStart(2, '0')}/{String(STEP_IDS.length).padStart(2, '0')}
          </div>
        </div>

        <div
          className="reveal tour-stepper"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${STEP_IDS.length}, 1fr)`,
            gap: 0,
            borderTop: '1px solid var(--p2a-dark-border)',
            borderBottom: '1px solid var(--p2a-dark-border)',
          }}
        >
          {STEP_IDS.map((id, i) => (
            <button
              key={id}
              onClick={() => setStep(i)}
              style={{
                padding: '18px 16px',
                textAlign: 'left',
                borderRight: i < STEP_IDS.length - 1 ? '1px solid var(--p2a-dark-border)' : 'none',
                background: step === i ? 'rgba(34,197,94,0.06)' : 'transparent',
                borderTop: `2px solid ${step === i ? 'var(--p2a-green-500)' : 'transparent'}`,
                marginTop: -1,
                color: step === i ? 'var(--p2a-dark-fg-1)' : 'var(--p2a-dark-fg-3)',
                transition: 'all var(--p2a-dur-base) var(--p2a-ease)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { if (step !== i) e.currentTarget.style.color = 'var(--p2a-dark-fg-1)'; }}
              onMouseLeave={(e) => { if (step !== i) e.currentTarget.style.color = 'var(--p2a-dark-fg-3)'; }}
            >
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: step === i ? 'var(--p2a-green-400)' : 'var(--p2a-dark-fg-4)' }}>
                {stepLabel(i)}
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, marginTop: 6 }}>{t(`home.product_tour.steps.${id}.title`)}</div>
            </button>
          ))}
        </div>

        <div
          className="reveal tour-preview"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 0,
            marginTop: 32,
            borderRadius: 6,
            overflow: 'hidden',
            border: '1px solid var(--p2a-dark-border-2)',
            background: 'var(--p2a-dark-panel)',
          }}
        >
          <div style={{ position: 'relative', minHeight: 480, background: '#0a0d11' }}>
            {STEP_IDS.map((id, i) => (
              <img
                key={id}
                src={STEP_IMG[id]}
                alt={t(`home.product_tour.steps.${id}.title`)}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  opacity: step === i ? 1 : 0,
                  transition: 'opacity 520ms var(--p2a-ease)',
                }}
              />
            ))}
            {['tl', 'tr', 'bl', 'br'].map((c) => (
              <span
                key={c}
                style={{
                  position: 'absolute',
                  width: 16, height: 16,
                  borderColor: 'var(--p2a-green-500)',
                  borderStyle: 'solid',
                  borderWidth:
                    c === 'tl' ? '1px 0 0 1px' :
                    c === 'tr' ? '1px 1px 0 0' :
                    c === 'bl' ? '0 0 1px 1px' :
                                 '0 1px 1px 0',
                  top: c.startsWith('t') ? 16 : 'auto',
                  bottom: c.startsWith('b') ? 16 : 'auto',
                  left: c.endsWith('l') ? 16 : 'auto',
                  right: c.endsWith('r') ? 16 : 'auto',
                }}
              />
            ))}
            <div style={{
              position: 'absolute', top: 16, right: 16,
              padding: '4px 10px',
              background: 'rgba(5,7,10,0.7)',
              border: '1px solid var(--p2a-dark-border-2)',
              borderRadius: 4,
              fontSize: 10,
              fontFamily: 'var(--p2a-font-mono)',
              letterSpacing: '0.14em',
              color: 'var(--p2a-green-400)',
            }}>
              {String(step + 1).padStart(2, '0')}
            </div>
          </div>

          <div style={{ padding: '40px 36px', display: 'flex', flexDirection: 'column' }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--p2a-green-400)', marginBottom: 16 }}>
              {stepLabel(step)}
            </div>
            <h3 className="h-2" style={{ marginBottom: 16 }}>{currentTitle}</h3>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--p2a-dark-fg-2)' }}>{currentDesc}</p>

            <div style={{ marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {currentTags.map((tag) => (
                <span key={tag} className="mono" style={{
                  fontSize: 11,
                  padding: '4px 10px',
                  border: '1px solid var(--p2a-dark-border-2)',
                  borderRadius: 999,
                  color: 'var(--p2a-dark-fg-2)',
                  letterSpacing: '0.02em',
                }}>{tag}</span>
              ))}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 32, display: 'flex', gap: 8 }}>
              <button
                aria-label={t('home.product_tour.prev')}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                style={{
                  width: 40, height: 40,
                  border: '1px solid var(--p2a-dark-border-2)',
                  borderRadius: 4,
                  color: 'var(--p2a-dark-fg-2)',
                  opacity: step === 0 ? 0.4 : 1,
                  cursor: step === 0 ? 'default' : 'pointer',
                }}
              >
                <Icon.ArrowRight width={14} height={14} style={{ transform: 'rotate(180deg)' }} />
              </button>
              <button
                aria-label={t('home.product_tour.next')}
                onClick={() => setStep((s) => Math.min(STEP_IDS.length - 1, s + 1))}
                disabled={step === STEP_IDS.length - 1}
                style={{
                  width: 40, height: 40,
                  border: '1px solid var(--p2a-dark-border-2)',
                  borderRadius: 4,
                  color: 'var(--p2a-dark-fg-1)',
                  background: step < STEP_IDS.length - 1 ? 'var(--p2a-green-500)' : 'transparent',
                  opacity: step === STEP_IDS.length - 1 ? 0.4 : 1,
                  cursor: step === STEP_IDS.length - 1 ? 'default' : 'pointer',
                }}
              >
                <Icon.ArrowRight
                  width={14}
                  height={14}
                  stroke={step < STEP_IDS.length - 1 ? '#022c11' : 'currentColor'}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .tour-stepper { grid-template-columns: repeat(2, 1fr) !important; }
          .tour-stepper button { border-right: 1px solid var(--p2a-dark-border) !important; }
          .tour-stepper button:nth-child(2n) { border-right: 0 !important; }
          .tour-preview { grid-template-columns: 1fr !important; }
          .tour-preview > div:first-child { min-height: 320px !important; }
        }
      `}</style>
    </section>
  );
}
